import type { PossibleDuJour } from '../../partage/api/types';
import { dureeNonPosee, echeanceLe, libereDesChoses, MOT_DEBLOQUANT, MOT_PRIORITAIRE, NE_RENTRE_PLUS } from '../../partage/vocabulaire';
import { dureeLisible, jourEnToutesLettres } from './datesDeLAgenda';
import { ajouter, type Choix } from './journeeEnCours';

/**
 * Les règles sans rendu de « Propose-moi une journée », au téléphone (chantier
 * `telephone-journee-agenda`, ticket 02, décisions d'Igor du 2026-10-05, 8, 9 et 20). Le serveur
 * propose, remplace (↻) et range ; ici, seulement ce que dit chaque ligne et les gestes sur la
 * liste, sans rien reclasser. La journée proposée est une possibilité : rien ne s'écrit avant
 * « C'est ma journée ».
 */

/** Une tâche de la proposition : un possible du jour, et s'il est Prioritaire ce jour-là (le serveur le dit). */
export interface TacheProposee extends PossibleDuJour {
  /** Absent pour une tâche ajoutée depuis la liste : on ne le sait pas, la ligne ne le dit pas. */
  prioritaire?: boolean;
}

/**
 * Le débloquant de la proposition : la tâche qui libère le plus, la première à égalité ; aucune si
 * rien ne libère. Le serveur met le sien en tête quand il tient ; sinon, c'est une autre.
 */
export function debloquantDe(taches: readonly Pick<PossibleDuJour, 'id' | 'libere'>[]): string | null {
  let meilleure: Pick<PossibleDuJour, 'id' | 'libere'> | null = null;
  for (const t of taches) if (t.libere > 0 && (!meilleure || t.libere > meilleure.libere)) meilleure = t;
  return meilleure?.id ?? null;
}

/**
 * Ce que dit la ligne d'une tâche, après son titre : « 1 h · débloquant », « 30 min · prioritaire ·
 * échéance le mardi 13 octobre », « 10 min · libère 2 choses ». Jamais « presse ».
 */
export function ceQuEstLaTache(t: TacheProposee, debloquant: string | null): string {
  return [
    t.estimee ? dureeNonPosee(dureeLisible(t.minutes)) : dureeLisible(t.minutes),
    t.id === debloquant ? MOT_DEBLOQUANT : t.libere > 0 ? libereDesChoses(t.libere) : null,
    t.prioritaire ? MOT_PRIORITAIRE : null,
    t.echeance ? echeanceLe(jourEnToutesLettres(t.echeance)) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** La proposition reçue devient la journée en cours : chaque tâche à la suite, dans son ordre. */
export function choixDeLaProposition(taches: readonly Pick<PossibleDuJour, 'id'>[]): Choix[] {
  return taches.map((t) => ({ id: t.id, fixee: null }));
}

/** ↻ : le remplaçant prend la place de la tâche remplacée, à la suite ; les autres ne bougent pas. */
export function remplacerDans(choix: readonly Choix[], ancien: string, nouveau: string): Choix[] {
  return choix.map((c) => (c.id === ancien ? { id: nouveau, fixee: null } : c));
}

/** Une tâche enlevée (×) ou remplacée (↻) : ↻ ne la ramène plus. */
export function ecarter(ecartees: readonly string[], id: string): string[] {
  return ecartees.includes(id) ? [...ecartees] : [...ecartees, id];
}

/** Ajouter une tâche à la suite si elle tient dans ce qui reste ; sinon rien ne change, et la phrase le dit. */
export function tacheAAjouter(choix: readonly Choix[], p: Pick<PossibleDuJour, 'id' | 'minutes'>, reste: number): { choix: Choix[]; message: string | null } {
  if (p.minutes > reste) return { choix: [...choix], message: NE_RENTRE_PLUS };
  return { choix: ajouter(choix, p.id), message: null };
}
